import {assertUnreachable} from '@wandb/weave/common/util/types';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {ToggleButtonGroup} from '@wandb/weave/components/ToggleButtonGroup';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import {create} from 'zustand';
import {useShallow} from 'zustand/react/shallow';

import {capitalizeFirstLetter} from '../../../../util/string';
import {EventTriggeringConditionType} from '../TriggerEventTypes';
import {Trigger} from '../TriggersTable/utilTypes';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {
  getRunFilterConfigFromTriggerFilter,
  getRunMetricRunFilterString,
  isValidRunFilterConfig,
  RunFilter,
  RunFilterChunk,
  RunFilterConfig,
} from './RunFilterShared';
import {
  AggOpOption,
  aggOpOptions,
  getAggOpStr,
  getNumberValueForField,
  isValidMetricName,
  isValidWindowSize,
  MetricFilterAggOp,
} from './RunMetricThresholdSection';
import {BoldLabel, ErrorText, SeparatedChunk} from './shared';

export type ChangeDirection = 'INCREASE' | 'DECREASE' | 'ANY';
export const isValidChangeDirection = (changeDirection: ChangeDirection) => {
  return (
    changeDirection === 'INCREASE' ||
    changeDirection === 'DECREASE' ||
    changeDirection === 'ANY'
  );
};

export type ChangeDirectionOption = {
  value: ChangeDirection;
  label: string;
};
export const changeDirectionOptions: ChangeDirectionOption[] = [
  {value: 'INCREASE', label: 'Increases by at least'},
  {value: 'DECREASE', label: 'Decreases by at least'},
  {value: 'ANY', label: 'Increases or decreases by at least'},
];

export const getChangeDirectionOption = (changeDirection: ChangeDirection) => {
  return changeDirectionOptions.find(
    option => option.value === changeDirection
  );
};

export type RunMetricChangeType = 'ABSOLUTE' | 'RELATIVE';
export const isValidChangeType = (changeType: RunMetricChangeType) => {
  return changeType === 'ABSOLUTE' || changeType === 'RELATIVE';
};
// The ToggleButtonGroup is weird because it's `value` is also the string label,
// so we need to store the enum value separately
export type ChangeTypeOption = {
  value: string; // shown in the UI
  enumValue: RunMetricChangeType; // what we use in the store
};
export const changeTypeOptions: ChangeTypeOption[] = [
  {value: 'Relative', enumValue: 'RELATIVE'},
  {value: 'Absolute', enumValue: 'ABSOLUTE'},
];
export const getChangeTypeOptionForToggleValue = (toggleValue: string) => {
  return changeTypeOptions.find(option => option.value === toggleValue);
};
export const getChangeTypeOptionForEnumValue = (
  enumValue: RunMetricChangeType
) => {
  return changeTypeOptions.find(option => option.enumValue === enumValue);
};
const defaultChangeType: RunMetricChangeType = 'RELATIVE';

export type MetricChangeFilter = {
  metricName: string;
  priorSize: string;
  currentSize: string;
  aggOp: MetricFilterAggOp;
  changeAmount: string;
  changeType: RunMetricChangeType;
  changeDirection: ChangeDirection;
  panelLink: string | undefined;
};

export type RunMetricChangeConfig = {
  metricFilter: Partial<MetricChangeFilter> &
    Pick<
      MetricChangeFilter,
      'priorSize' | 'currentSize' | 'aggOp' | 'changeType' | 'changeDirection'
    >;
  runFilter: RunFilterConfig;
  allowAutoFocus: boolean;
};

const DEFAULT_PRIOR_WINDOW_SIZE = 50;
const DEFAULT_CURRENT_WINDOW_SIZE = 10;

const initialRunMetricConfig: RunMetricChangeConfig = {
  metricFilter: {
    metricName: undefined,
    priorSize: DEFAULT_PRIOR_WINDOW_SIZE.toString(),
    currentSize: DEFAULT_CURRENT_WINDOW_SIZE.toString(),
    aggOp: 'AVERAGE',
    changeAmount: undefined,
    changeType: defaultChangeType,
    changeDirection: 'ANY',
  },
  runFilter: {
    isRunNameFilterEnabled: false,
    runName: undefined,
    isUsernameFilterEnabled: false,
    username: undefined,
  },
  allowAutoFocus: true,
};

export const useRunMetricChangeStore = create<RunMetricChangeConfig>()(() => ({
  ...initialRunMetricConfig,
}));

export const RunMetricChangeActions = {
  initAsync: async (suggestedMetricName?: string, panelLink?: string) => {
    useRunMetricChangeStore.setState({
      ...initialRunMetricConfig,
      metricFilter: {
        ...initialRunMetricConfig.metricFilter,
        metricName: suggestedMetricName,
        panelLink: panelLink,
      },
      allowAutoFocus: suggestedMetricName == null,
    });
  },
  initFromTrigger: (trigger: Trigger) => {
    const config = getRunMetricChangeConfig(trigger);
    if (config) {
      useRunMetricChangeStore.setState(config);
    }
  },

  setMetricName: (metricName: string) => {
    setMetricFilter({metricName});
  },
  setAggOp: (aggOp: MetricFilterAggOp) => {
    setMetricFilter({aggOp});
  },
  setChangeAmount: (changeAmount: string) => {
    // note that for the % type, we store the raw value for now (eg 20 for 20%),
    // will translate it to % when saving to API
    setMetricFilter({changeAmount});
  },
  setRunName: (runName: string) => {
    setRunFilter({runName});
  },
  setPriorSize: (priorSize: string) => {
    setMetricFilter({priorSize});
  },
  setCurrentSize: (currentSize: string) => {
    setMetricFilter({currentSize});
  },
  setIsRunNameFilterEnabled: (isRunNameFilterEnabled: boolean) => {
    setRunFilter({isRunNameFilterEnabled});
  },
  setChangeDirection: (changeDirection: ChangeDirection) => {
    setMetricFilter({changeDirection});
  },
  setChangeType: (changeType: RunMetricChangeType) => {
    setMetricFilter({changeType});
  },
  setUsername: (username: string | undefined) => {
    setRunFilter({username});
  },
  setIsUsernameFilterEnabled: (isUsernameFilterEnabled: boolean) => {
    setRunFilter({isUsernameFilterEnabled});
  },
};

const setMetricFilter = (metricFilter: Partial<MetricChangeFilter>) => {
  useRunMetricChangeStore.setState(state => ({
    metricFilter: {...state.metricFilter, ...metricFilter},
  }));
};

const setRunFilter = (runFilter: Partial<RunFilter>) => {
  useRunMetricChangeStore.setState(state => ({
    runFilter: {...state.runFilter, ...runFilter},
  }));
};

export const isValidChangeAmount = (changeAmount: string | undefined) => {
  if (changeAmount === undefined || changeAmount === '') {
    return false;
  }
  const changeAmountNumber = getNumberValueForField(changeAmount);
  return !isNaN(changeAmountNumber) && isFinite(changeAmountNumber);
};

export const isValidRunMetricChangeConfig = (config: RunMetricChangeConfig) => {
  return (
    isValidMetricName(config.metricFilter.metricName) &&
    isValidChangeAmount(config.metricFilter.changeAmount) &&
    isValidWindowSize(config.metricFilter.priorSize) &&
    isValidWindowSize(config.metricFilter.currentSize) &&
    // aggOp is currently hardcoded. For the futurenote that undefined is a valid
    //   value for aggOp - it means "take the last value and ignore windowSize"
    //   which is not something the UI currently supports, but it's not an error
    isValidChangeDirection(config.metricFilter.changeDirection) &&
    isValidChangeType(config.metricFilter.changeType) &&
    isValidRunFilterConfig(config.runFilter)
  );
};

export const getEventConfigForRunMetricChange = (
  config: RunMetricChangeConfig
) => {
  return {
    eventTriggeringConditionType: EventTriggeringConditionType.RunMetric,
    filters: {
      filters: [
        {
          metricFilter: {...config.metricFilter, aggOp: 'AVERAGE'},
          runFilter: config.runFilter,
        },
      ],
    },
  };
};

export const RunMetricChangeSection = () => {
  const metricFilter = useRunMetricChangeStore(
    useShallow(state => state.metricFilter)
  );
  const runFilter = useRunMetricChangeStore(
    useShallow(state => state.runFilter)
  );

  // the undefined checks here prevent showing the error before user has configured that field
  const showMetricNameError =
    metricFilter.metricName !== undefined &&
    !isValidMetricName(metricFilter.metricName);
  const showPriorSizeError =
    metricFilter.priorSize !== undefined &&
    !isValidWindowSize(metricFilter.priorSize);
  const showCurrentSizeError =
    metricFilter.currentSize !== undefined &&
    !isValidWindowSize(metricFilter.currentSize);

  const eventDescription = capitalizeFirstLetter(
    getRunMetricChangeString(metricFilter, runFilter)
  );

  const allowAutoFocus = useRunMetricChangeStore(state => state.allowAutoFocus);

  const currentAggOpOption =
    aggOpOptions.find(option => option.value === metricFilter.aggOp) ?? null; // note that null tells react-select to show the placeholder, undefined will not

  return (
    <Tailwind>
      <div className="mt-16 font-semibold">Metric setup</div>
      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-moon-200 p-16">
        <S.AutomationDescriptionSection>
          <S.AutomationDescriptionIcon>
            <Icon name="recent-clock" />
          </S.AutomationDescriptionIcon>
          <S.AutomationDescriptionBody>
            <S.AutomationDescriptionTitle>
              In the event that
            </S.AutomationDescriptionTitle>
            <S.AutomationDescriptionText>
              {eventDescription}
            </S.AutomationDescriptionText>
          </S.AutomationDescriptionBody>
        </S.AutomationDescriptionSection>
        <SeparatedChunk>
          <BoldLabel>Metric</BoldLabel>
          <div className="mt-4">
            <TextField
              value={metricFilter.metricName || ''}
              onChange={value => RunMetricChangeActions.setMetricName(value)}
              placeholder="Ex. loss"
              autoFocus={
                /* We don't want to auto focus the metric name field when we're in the workspace drawer -
            it causes jankiness when the drawer opens (the text field is focused and it forces the drawer open
            before the animation is complete). We are using "is the metric name field populated" as a proxy here,
            since it makes sense not to auto focus a field that's already been filled in anyway. */
                allowAutoFocus
              }
            />
            {showMetricNameError && (
              <ErrorText>Metric name cannot be empty</ErrorText>
            )}
          </div>
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center">
              Current window
              <div className="ml-4">
                <Tooltip
                  content="Integer 1 - 100 required."
                  trigger={
                    <Icon className="inline-block text-moon-400" name="info" />
                  }
                />
              </div>
            </div>
          </BoldLabel>
          <div className="mt-4 flex items-center">
            <Select<AggOpOption>
              options={aggOpOptions}
              placeholder="Select an aggregation operation..."
              value={currentAggOpOption}
              onChange={(aggOp: AggOpOption | null) => {
                if (aggOp) {
                  RunMetricChangeActions.setAggOp(aggOp.value);
                }
              }}
              autoFocus={true}
              className="mr-8"
            />{' '}
            of
            <span className="mx-8 inline-block w-64">
              <TextField
                type="number"
                value={metricFilter.currentSize.toString()}
                onChange={value => {
                  RunMetricChangeActions.setCurrentSize(value);
                }}
                placeholder="Ex. 5"
                errorState={showCurrentSizeError}
              />
            </span>
            logged values
          </div>
          {showCurrentSizeError && (
            <ErrorText>Integer 1 - 100 required</ErrorText>
          )}
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center">
              Prior window
              <div className="ml-4">
                <Tooltip
                  content="Integer 1 - 100 required."
                  trigger={
                    <Icon className="inline-block text-moon-400" name="info" />
                  }
                />
              </div>
            </div>
          </BoldLabel>
          <div className="mt-4 flex items-center">
            <Select<AggOpOption>
              options={aggOpOptions}
              placeholder="Select an aggregation operation..."
              value={currentAggOpOption}
              onChange={(aggOp: AggOpOption | null) => {
                if (aggOp) {
                  RunMetricChangeActions.setAggOp(aggOp.value);
                }
              }}
              autoFocus={true}
              className="mr-8"
            />{' '}
            of
            <span className="mx-8 inline-block w-64">
              <TextField
                type="number"
                value={metricFilter.priorSize.toString()}
                onChange={value => {
                  RunMetricChangeActions.setPriorSize(value);
                }}
                placeholder="Ex. 5"
                errorState={showPriorSizeError}
              />
            </span>
            logged values prior to the current window
          </div>
          {showPriorSizeError && (
            <ErrorText>Integer 1 - 100 required</ErrorText>
          )}
        </SeparatedChunk>
        <MetricThresholdConfig />
        <SeparatedChunk>
          <RunFilterChunk
            runFilter={runFilter}
            actions={RunMetricChangeActions}
          />
        </SeparatedChunk>
      </div>
    </Tailwind>
  );
};

// this is used when we're looking at an already created trigger in the UI
export const getRunMetricChangeConfig = (
  trigger: Trigger
): RunMetricChangeConfig | undefined => {
  if (
    !trigger.triggeringCondition.filter ||
    trigger.triggeringCondition.eventType !==
      EventTriggeringConditionType.RunMetricChange
  ) {
    return undefined;
  }

  try {
    const parsedFilter = JSON.parse(trigger.triggeringCondition.filter);
    const runFilter = getRunFilterConfigFromTriggerFilter(parsedFilter);
    if (!runFilter) {
      console.error(
        'Failed to parse run metric change config from trigger:',
        trigger
      );
      return undefined;
    }
    const metricFilter = parsedFilter.run_metric_filter.change_filter;
    if (!metricFilter) {
      console.error(
        'Failed to parse run metric change config from trigger:',
        trigger
      );
      return undefined;
    }

    let numChangeAmount = metricFilter.change_amount;
    // this makes displaying/editing the percentage value easier
    if (metricFilter.change_type === 'RELATIVE') {
      numChangeAmount = numChangeAmount * 100;
    }

    return {
      metricFilter: {
        metricName: metricFilter.name,
        priorSize: metricFilter.prior_window_size.toString(),
        currentSize: metricFilter.current_window_size.toString(),
        aggOp: metricFilter.agg_op,
        changeAmount: numChangeAmount.toString(),
        changeType: metricFilter.change_type,
        changeDirection: metricFilter.change_dir,
        panelLink: metricFilter.panel_link,
      },
      runFilter,
      allowAutoFocus: false,
    };
  } catch (e) {
    console.error('Failed to parse run metric change config from trigger:', e);
    return undefined;
  }
};

export const getDirectionAndAmountString = (
  metricFilter: RunMetricChangeConfig['metricFilter']
) => {
  let changeAmountStr = metricFilter.changeAmount
    ? `${metricFilter.changeAmount}`
    : '{Metric threshold}';
  if (metricFilter.changeType === 'RELATIVE') {
    changeAmountStr = `${changeAmountStr}%`;
  }
  let changeDirectionStr;
  switch (metricFilter.changeDirection) {
    case 'INCREASE':
      changeDirectionStr = 'increases';
      break;
    case 'DECREASE':
      changeDirectionStr = 'decreases';
      break;
    case 'ANY':
      changeDirectionStr = 'increases or decreases';
      break;
    default:
      assertUnreachable(metricFilter.changeDirection);
  }
  return {
    changeDirectionStr,
    changeAmountStr,
  };
};
export const getRunMetricChangeString = (
  metricFilter: RunMetricChangeConfig['metricFilter'],
  runFilter: RunMetricChangeConfig['runFilter']
) => {
  let ret = getRunMetricRunFilterString(runFilter);
  const aggOpStr = getAggOpStr(metricFilter.aggOp);

  const metricNameStr = metricFilter.metricName
    ? `${metricFilter.metricName}`
    : '{Metric}';

  const currentSizeStr = metricFilter.currentSize
    ? `${metricFilter.currentSize}`
    : '{Current window}';
  const priorSizeStr = metricFilter.priorSize
    ? `${metricFilter.priorSize}`
    : '{Prior window}';

  const {changeDirectionStr, changeAmountStr} =
    getDirectionAndAmountString(metricFilter);

  ret +=
    `${aggOpStr} of ${metricNameStr} for the last ${currentSizeStr} ` +
    `logged values compared to the ${aggOpStr} of ${metricNameStr} during the prior ${priorSizeStr} logged values ` +
    `${changeDirectionStr} by at least` +
    ` ${changeAmountStr}.`;
  return ret;
};

export const MetricThresholdConfig = () => {
  const metricFilter = useRunMetricChangeStore(
    useShallow(state => state.metricFilter)
  );
  const showChangeAmountError =
    metricFilter.changeAmount !== undefined &&
    !isValidChangeAmount(metricFilter.changeAmount);
  return (
    <>
      <SeparatedChunk>
        <div className="mb-8 mt-4 flex flex-row gap-16">
          <div className="flex-1 font-semibold">Metric threshold</div>
          <ToggleButtonGroup
            options={changeTypeOptions}
            value={
              getChangeTypeOptionForEnumValue(metricFilter.changeType)!.value
            }
            size="small"
            onValueChange={toggleValue =>
              RunMetricChangeActions.setChangeType(
                getChangeTypeOptionForToggleValue(toggleValue)!.enumValue
              )
            }
          />
        </div>

        <div className="mt-4 flex flex-row gap-16">
          <div className="flex-2">
            <Select<ChangeDirectionOption>
              value={getChangeDirectionOption(metricFilter.changeDirection)}
              onChange={e =>
                RunMetricChangeActions.setChangeDirection(
                  e?.value || 'INCREASE'
                )
              }
              options={changeDirectionOptions}
            />
          </div>
          <div className="flex-1">
            <TextField
              value={metricFilter.changeAmount || ''}
              onChange={value => RunMetricChangeActions.setChangeAmount(value)}
              placeholder="Ex. 0.9"
              errorState={showChangeAmountError}
            />
          </div>
          {metricFilter.changeType === 'RELATIVE' && (
            <div className="flex-0">%</div>
          )}
        </div>
        {showChangeAmountError && (
          <S.ErrorText>Change amount must be a valid number</S.ErrorText>
        )}
      </SeparatedChunk>
    </>
  );
};
